import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Wallet, TrendingUp, Repeat, UserPlus, Pencil, Check, X } from "lucide-react";

const fade = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };
const money = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(n || 0);

// Panel "Mi consultorio": costo de consulta editable + ganancias estimadas.
// Reutilizable: si recibe `stats` lo usa; si no, se autoconsulta /stats.
export function MiConsultorio({ stats: statsProp, onSaved, delay = 0.09 }) {
  const { user, setUser } = useAuth();
  const [stats, setStats] = useState(statsProp || null);
  const [editing, setEditing] = useState(false);
  const [feeInput, setFeeInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (statsProp !== undefined) setStats(statsProp); }, [statsProp]);
  useEffect(() => { if (statsProp === undefined) api.get("/stats").then((r) => setStats(r.data)).catch(() => {}); }, [statsProp]);

  const start = () => { setFeeInput(String(stats?.consultation_fee || user?.consultation_fee || "")); setEditing(true); };
  const save = async () => {
    const val = parseFloat(feeInput);
    if (isNaN(val) || val < 0) { toast.error("Escribe un monto válido"); return; }
    setSaving(true);
    try {
      const { data: u } = await api.put("/doctor/profile", { consultation_fee: val });
      setUser(u); setEditing(false);
      if (onSaved) onSaved();
      else { const r = await api.get("/stats"); setStats(r.data); }
      toast.success("Costo de consulta actualizado");
    } catch { toast.error("No se pudo guardar"); }
    finally { setSaving(false); }
  };

  return (
    <motion.div {...fade} transition={{ delay }} data-testid="crm-section"
      className="bg-gradient-to-br from-brand-ink to-[#2a2d47] rounded-3xl p-6 shadow-[0_8px_30px_rgb(61,64,91,0.12)] text-white">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <h2 className="font-outfit text-xl font-bold flex items-center gap-2"><Wallet size={20} className="text-brand-sage" /> Mi consultorio</h2>
        <div className="flex items-center gap-2" data-testid="fee-control">
          {!editing ? (
            <button data-testid="fee-edit-btn" onClick={start}
              className="flex items-center gap-1.5 text-sm font-semibold bg-white/10 hover:bg-white/20 px-3 py-2 rounded-xl transition-all">
              <Pencil size={14} /> Consulta: <span className="font-extrabold">{money(stats?.consultation_fee)}</span>
            </button>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-white/70">$</span>
              <input data-testid="fee-input" type="number" min="0" value={feeInput} autoFocus
                onChange={(e) => setFeeInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
                placeholder="Costo por consulta"
                className="w-32 bg-white/95 text-brand-ink rounded-xl px-3 py-2 text-sm outline-none" />
              <button data-testid="fee-save-btn" onClick={save} disabled={saving}
                className="flex items-center gap-1 text-sm font-bold bg-brand-sage hover:opacity-90 text-white px-3 py-2 rounded-xl transition-all disabled:opacity-60"><Check size={15} /> Guardar</button>
              <button data-testid="fee-cancel-btn" onClick={() => setEditing(false)} className="text-white/60 hover:text-white p-1"><X size={16} /></button>
            </div>
          )}
        </div>
      </div>

      {(!stats?.consultation_fee) && !editing && (
        <button data-testid="fee-set-hint" onClick={start}
          className="w-full mb-4 text-left text-sm text-white/80 bg-white/5 hover:bg-white/10 rounded-2xl px-4 py-3 transition-all">
          💡 Define <b>cuánto cuesta tu consulta</b> para estimar tus ganancias automáticamente. Toca aquí para configurarlo.
        </button>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white/10 rounded-2xl p-4" data-testid="crm-earnings-month">
          <div className="flex items-center gap-1.5 text-white/70 text-xs font-semibold mb-1"><TrendingUp size={14} /> Ganancia del mes</div>
          <p className="font-outfit text-2xl sm:text-3xl font-extrabold">{money(stats?.earnings_month)}</p>
          <p className="text-[11px] text-white/60 mt-0.5">{stats?.consultations_month || 0} consulta(s) este mes</p>
        </div>
        <div className="bg-white/10 rounded-2xl p-4" data-testid="crm-earnings-total">
          <div className="flex items-center gap-1.5 text-white/70 text-xs font-semibold mb-1"><Wallet size={14} /> Ganancia total</div>
          <p className="font-outfit text-2xl sm:text-3xl font-extrabold">{money(stats?.earnings_total)}</p>
          <p className="text-[11px] text-white/60 mt-0.5">{stats?.consultations_total || 0} consulta(s)</p>
        </div>
        <div className="bg-white/10 rounded-2xl p-4" data-testid="crm-new">
          <div className="flex items-center gap-1.5 text-white/70 text-xs font-semibold mb-1"><UserPlus size={14} /> Nuevos</div>
          <p className="font-outfit text-2xl sm:text-3xl font-extrabold">{stats?.new_patients || 0}</p>
          <p className="text-[11px] text-white/60 mt-0.5">1 sola consulta</p>
        </div>
        <div className="bg-white/10 rounded-2xl p-4" data-testid="crm-returning">
          <div className="flex items-center gap-1.5 text-white/70 text-xs font-semibold mb-1"><Repeat size={14} /> Recurrentes</div>
          <p className="font-outfit text-2xl sm:text-3xl font-extrabold">{stats?.returning_patients || 0}</p>
          <p className="text-[11px] text-white/60 mt-0.5">vuelven a consulta</p>
        </div>
      </div>
      <p className="text-[11px] text-white/50 mt-3">Estimación basada en tus consultas registradas × costo de consulta. Solo tú ves esta información.</p>
    </motion.div>
  );
}
